import React from 'react';
import PropTypes from 'prop-types';
import { useAppDispatch, useAppSelector } from '../../store/hooks';

import Snackbar, { SnackbarProps as MuiSnackbarProps } from '@mui/material/Snackbar';
import { Box, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { resetFlashMessage } from '../../store/actions/flashMessage';

export interface FlashMessageAction {
  label: string;
  onClick: () => void;
}

export type FlashMessageProps = MuiSnackbarProps & {
  icon?: React.ReactNode;
  primaryAction?: FlashMessageAction;
  secondaryAction?: FlashMessageAction;
};

export const FlashMessage = ({
  className,
  icon,
  primaryAction,
  secondaryAction,
}: FlashMessageProps) => {
  const dispatch: any = useAppDispatch();
  const { flashMessage } = useAppSelector(state => ({
    flashMessage: state.flashMessageReducer,
  }));

  const handleClose = () => {
    dispatch(resetFlashMessage());
  };

  return (
    <Snackbar
      className={className}
      open={flashMessage.open}
      autoHideDuration={6000}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      sx={{ zIndex: 120 }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pl: 2,
          pr: 1,
          py: '6px',
          borderRadius: '8px',
          background: globalColours['$global-colour-secondary-brand-4'],
          boxShadow: '0px 0px 20px rgba(69, 71, 69, 0.2)',
          maxWidth: { xs: '100%', md: '600px' },
        }}
      >
        {/* Leading icon */}
        {flashMessage.icon && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', flexShrink: 0 }}>
            {flashMessage.icon}
          </Box>
        )}

        {/* Message */}
        <Typography
          variant='body3'
          sx={{
            color: globalColours['$global-colour-base-contrast'],
            flexShrink: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {flashMessage.message}
        </Typography>

        {/* Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {/* Secondary action — underlined text button */}
          {flashMessage.secondaryAction && (
            <Box
              component='button'
              onClick={flashMessage.secondaryAction.onClick}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                p: 1,
                borderRadius: '2px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
              }}
            >
              <Typography
                variant='body3SemiBold'
                sx={{
                  color: globalColours['$global-colour-interactive-control-default'],
                  textDecoration: 'underline',
                  letterSpacing: '-0.25px',
                  whiteSpace: 'nowrap',
                }}
              >
                {flashMessage.secondaryAction.label}
              </Typography>
            </Box>
          )}

          {/* Primary action — bordered button */}
          {flashMessage.primaryAction && (
            <Box
              component='button'
              onClick={flashMessage.primaryAction.onClick}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                p: '4px',
                borderRadius: '4px',
                border: `1px solid ${globalColours['$global-colour-interactive-control-default']}`,
                background: 'transparent',
                cursor: 'pointer',
              }}
            >
              <Typography
                variant='body3SemiBold'
                sx={{
                  color: globalColours['$global-colour-interactive-control-default'],
                  whiteSpace: 'nowrap',
                }}
              >
                {flashMessage.primaryAction.label}
              </Typography>
            </Box>
          )}

          {/* Close icon button */}
          <IconButton
            onClick={handleClose}
            size='small'
            sx={{
              p: '2px',
              color: globalColours['$global-colour-base-contrast'],
              '&:hover': { background: 'transparent', opacity: 0.8 },
            }}
          >
            <CloseIcon sx={{ width: '16px', height: '16px' }} />
          </IconButton>
        </Box>
      </Box>
    </Snackbar>
  );
};

FlashMessage.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  icon: PropTypes.node,
  primaryAction: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  }),
  secondaryAction: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  }),
};

FlashMessage.defaultProps = {
  children: null,
  className: null,
  icon: null,
  primaryAction: null,
  secondaryAction: null,
};

export default styled<any>(FlashMessage)(() => ``);
